import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Type, Sparkles } from 'lucide-react';

export type ImageMode = 'text-to-image' | 'reference-images';

interface ImageModeSelectorProps {
  value: ImageMode;
  onChange: (mode: ImageMode) => void;
}

/**
 * ImageModeSelector - Mode selection for AI image generation
 *
 * Modes:
 * - Text-to-Image: Generate completely new image from text prompt
 * - Reference Images: Compose new image from 1-10 existing images
 *
 * Styled to match internal app with blue background when selected
 */
export const ImageModeSelector: React.FC<ImageModeSelectorProps> = ({
  value,
  onChange
}) => {
  return (
    <div className="space-y-2">
      <Label>Generation Mode</Label>
      <RadioGroup
        value={value}
        onValueChange={(val) => onChange(val as ImageMode)}
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
      >
        {/* Text-to-Image */}
        <div>
          <RadioGroupItem
            value="text-to-image"
            id="text-to-image"
            className="peer sr-only"
          />
          <Label
            htmlFor="text-to-image"
            className="flex flex-col items-center justify-center space-y-3 rounded-lg border-2 border-muted p-6 cursor-pointer hover:border-muted-foreground/40 transition-all peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 h-full min-h-[140px]"
          >
            <Type className="h-12 w-12 text-muted-foreground peer-data-[state=checked]:text-primary" />
            <div className="text-center space-y-1">
              <p className="font-semibold">Text → Image</p>
              <p className="text-xs text-muted-foreground">
                Generate image from text description using AI
              </p>
            </div>
          </Label>
        </div>

        {/* Reference Images */}
        <div>
          <RadioGroupItem
            value="reference-images"
            id="reference-images"
            className="peer sr-only"
          />
          <Label
            htmlFor="reference-images"
            className="flex flex-col items-center justify-center space-y-3 rounded-lg border-2 border-muted p-6 cursor-pointer hover:border-muted-foreground/40 transition-all peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/10 h-full min-h-[140px]"
          >
            <Sparkles className="h-12 w-12 text-muted-foreground" />
            <div className="text-center space-y-1">
              <p className="font-semibold">Reference Images</p>
              <p className="text-xs text-muted-foreground">
                Blend multiple images together or use a single image as a reference
              </p>
            </div>
          </Label>
        </div>
      </RadioGroup>
      <p className="text-xs text-muted-foreground">
        Select how you want to generate your image
      </p>
    </div>
  );
};

export default ImageModeSelector;
